import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Building2, ArrowLeft, Sparkles, Wrench, Bug } from 'lucide-react';
import { releaseNotes, APP_VERSION } from '../data/releaseNotes';

const TYPE_META = {
  new: { label: 'New', icon: Sparkles, badge: 'bg-orange-100 text-orange-700 border-orange-200' },
  improved: { label: 'Improved', icon: Wrench, badge: 'bg-blue-100 text-blue-700 border-blue-200' },
  fixed: { label: 'Fixed', icon: Bug, badge: 'bg-green-100 text-green-700 border-green-200' },
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

export default function WhatsNew() {
  // Mark the latest version as seen (clears the "new" dot in Settings for signed-in users).
  useEffect(() => {
    try {
      localStorage.setItem('corbel_last_seen_version', APP_VERSION);
    } catch {
      /* ignore storage errors (e.g. private mode) */
    }
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="whats-new-page">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="whats-new-home-link">
              <div className="h-8 w-8 bg-orange-500 rounded-sm flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-lg">Corbel</span>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" size="sm" className="rounded-sm" data-testid="whats-new-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to app
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="inline-flex items-center gap-2 bg-orange-500/15 text-orange-400 px-3 py-1 rounded-full text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" /> Product updates
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">What's New in Corbel</h1>
          <p className="text-slate-300 text-base md:text-lg max-w-2xl">
            The latest features and improvements we've shipped to help you run your construction
            business. Currently on version {APP_VERSION}.
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {releaseNotes.map((release, idx) => (
            <div
              key={release.version}
              className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4 md:gap-8"
              data-testid={`release-${release.version}`}
            >
              {/* Left: version + date */}
              <div className="md:text-right md:pt-6">
                <div className="flex md:justify-end items-center gap-2">
                  <span className="text-2xl font-bold text-slate-900">v{release.version}</span>
                  {idx === 0 && (
                    <Badge className="bg-orange-500 text-white border-0">Latest</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{formatDate(release.date)}</p>
              </div>

              {/* Right: card */}
              <Card className="rounded-sm border-l-4 border-l-orange-500">
                <CardContent className="p-6">
                  <h2 className="text-lg font-bold text-slate-900">{release.title}</h2>
                  {release.summary && (
                    <p className="text-muted-foreground mt-1 mb-4 text-sm">{release.summary}</p>
                  )}
                  <ul className="space-y-3">
                    {release.changes.map((change, cIdx) => {
                      const meta = TYPE_META[change.type] || TYPE_META.new;
                      const Icon = meta.icon;
                      return (
                        <li key={`${release.version}-${cIdx}`} className="flex items-start gap-3">
                          <Badge
                            variant="outline"
                            className={`${meta.badge} shrink-0 mt-0.5 gap-1 font-medium`}
                          >
                            <Icon className="h-3 w-3" /> {meta.label}
                          </Badge>
                          <span className="text-sm text-slate-700">{change.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground mt-16 border-t pt-8">
          <p>Have an idea or request? We'd love to hear it.</p>
          <Link to="/contact" className="text-orange-600 font-medium hover:underline" data-testid="whats-new-contact-link">
            Send us feedback
          </Link>
        </div>
      </div>
    </div>
  );
}
